'use client'

import { useEffect, useState, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { ChevronDown, ChevronUp, X, Search, ArrowUpDown, ArrowLeft, MapPin } from 'lucide-react'
import type { CityDatum } from '@/app/markets/page'
import { fetchCityClimate } from '@/app/actions'
import WeatherStrip from '@/app/components/WeatherStrip'
import type { CityClimate } from '@/app/components/WeatherStrip'

const CityLocationMap = dynamic(() => import('@/app/components/CityLocationMap'), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center rounded-lg border border-[var(--border)] bg-[var(--surface-1)]" style={{ minHeight: '280px' }}>
      <p className="text-sm text-[var(--text-3)]">Loading map...</p>
    </div>
  ),
})

/* ── Tier thresholds ─────────────────────────────────── */
const MANY_AREAS_THRESHOLD = 10 // 10+ → enhanced area picker (search, sort, elevated)

type Props = {
  city: CityDatum
  description?: string | null
  childCities?: CityDatum[]
  loadingChildren?: boolean
  onExpandChildren?: () => void
  onClose: () => void
  onStartScenario?: () => void
}

const formatPopulation = (value: number | null) => {
  if (!value) return 'N/A'
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`
  return value.toString()
}

type SortKey = 'name' | 'price-asc' | 'price-desc'

export default function CityDetailModalMarkets({
  city,
  description,
  childCities,
  loadingChildren,
  onExpandChildren,
  onClose,
  onStartScenario,
}: Props) {
  const router = useRouter()
  const [showCityOverview, setShowCityOverview] = useState(false)
  const [showChildren, setShowChildren] = useState(false)
  const [climate, setClimate] = useState<CityClimate | null>(null)
  const [areaSearch, setAreaSearch] = useState('')
  const [areaSort, setAreaSort] = useState<SortKey>('name')
  const [selectedChild, setSelectedChild] = useState<CityDatum | null>(null)

  const hasChildren = city.child_count > 0
  const isManyAreas = city.child_count >= MANY_AREAS_THRESHOLD

  /* Auto-load children for large cities on mount */
  useEffect(() => {
    if (isManyAreas && onExpandChildren && !childCities) {
      onExpandChildren()
    }
  }, [isManyAreas, onExpandChildren, childCities])

  useEffect(() => {
    fetchCityClimate(city.id).then((data) => {
      if (data) setClimate(data)
    })
  }, [city.id])

  const handleToggleChildren = () => {
    if (!showChildren && onExpandChildren && !childCities) {
      onExpandChildren()
    }
    setShowChildren((prev) => !prev)
  }

  const handleStartScenario = () => {
    if (onStartScenario) {
      onStartScenario()
    } else if (city.primary_zip) {
      router.push(`/calculator/transition?targetZip=${encodeURIComponent(city.primary_zip)}`)
    }
  }

  /* ── Filtered + sorted area list ─────────────────── */
  const filteredAreas = useMemo(() => {
    if (!childCities) return []
    let list = [...childCities]

    // Filter by search
    if (areaSearch.trim()) {
      const q = areaSearch.trim().toLowerCase()
      list = list.filter((c) => c.primary_city.toLowerCase().includes(q))
    }

    // Sort
    if (areaSort === 'name') {
      list.sort((a, b) => a.primary_city.localeCompare(b.primary_city))
    } else if (areaSort === 'price-asc') {
      list.sort((a, b) => (a.median_home_price ?? Infinity) - (b.median_home_price ?? Infinity))
    } else if (areaSort === 'price-desc') {
      list.sort((a, b) => (b.median_home_price ?? 0) - (a.median_home_price ?? 0))
    }

    return list
  }, [childCities, areaSearch, areaSort])

  /* ── Area card component ─────────────────────────── */
  const AreaCard = ({ child }: { child: CityDatum }) => (
    <div
      key={child.id}
      className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-4 flex items-center justify-between hover:bg-[var(--surface-2)] transition cursor-pointer group"
      onClick={() => setSelectedChild(child)}
    >
      <div className="min-w-0">
        <p className="font-medium text-[var(--text-1)] truncate">{child.primary_city}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {child.median_home_price !== null && (
            <span className="text-sm text-[var(--text-3)]">
              ${Math.round(child.median_home_price).toLocaleString()}
            </span>
          )}
          {child.population != null && (
            <span className="text-xs text-[var(--text-3)]">
              Pop. {formatPopulation(child.population)}
            </span>
          )}
        </div>
      </div>
      <span className="shrink-0 ml-3 rounded-full bg-[var(--accent)] px-3 py-1.5 text-xs font-semibold text-[var(--accent-contrast)] transition group-hover:bg-[var(--accent-hover)]">
        Explore
      </span>
    </div>
  )

  /* ── Child City Detail Panel ───────────────────────
     Shows detailed info about a selected child area
     with a CTA to start a financial scenario.
  */
  const ChildCityDetail = ({ child }: { child: CityDatum }) => {
    const hasEconomicData = child.median_home_price != null || child.median_household_income != null
    const hasCharacteristics = child.crime_score != null || child.school_score != null ||
      child.eco_score != null || child.lgbtq_score != null || child.epa_aqi_median != null ||
      child.nearest_va_distance_miles != null || child.affordability_ratio != null ||
      (child.pct_public_transit ?? 0) + (child.pct_walk_bike ?? 0) > 0

    return (
      <div className="space-y-6">
        {/* Back button + header */}
        <div>
          <button
            onClick={() => setSelectedChild(null)}
            className="inline-flex items-center gap-1.5 text-sm text-[var(--accent)] hover:text-[var(--accent-hover)] transition mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Areas
          </button>

          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold text-[var(--text-1)]">
                {child.primary_city}
              </h2>
              <p className="text-lg text-[var(--text-2)] mt-1">
                {child.primary_state}
                {child.population != null && ` · Pop. ${formatPopulation(child.population)}`}
              </p>
            </div>
            <MapPin className="h-6 w-6 text-[var(--text-3)] shrink-0 mt-1" />
          </div>
        </div>

        {/* Hero image if available */}
        {child.hero_image_url && (
          <div className="relative h-48 rounded-lg overflow-hidden">
            <img
              src={child.hero_image_url}
              alt={`${child.primary_city}`}
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
            />
          </div>
        )}

        {/* Description */}
        {child.description && (
          <section>
            <p className="text-[var(--text-2)] leading-relaxed">{child.description}</p>
          </section>
        )}

        {/* Map */}
        {child.lat != null && child.lng != null && (
          <section>
            <CityLocationMap
              lat={child.lat}
              lng={child.lng}
              cityName={`${child.primary_city}, ${child.primary_state}`}
              className="h-[240px]"
            />
          </section>
        )}

        {/* Economic stats */}
        {hasEconomicData && (
          <section className="space-y-3">
            <h3 className="text-xl font-bold text-[var(--text-1)]">Economics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {child.median_home_price != null && (
                <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-4">
                  <p className="text-xs font-medium text-[var(--text-2)] uppercase">Median Home</p>
                  <p className="mt-1 text-xl font-bold text-[var(--text-1)]">
                    ${Math.round(child.median_home_price).toLocaleString()}
                  </p>
                </div>
              )}
              {child.median_household_income != null && (
                <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-4">
                  <p className="text-xs font-medium text-[var(--text-2)] uppercase">Household Income</p>
                  <p className="mt-1 text-xl font-bold text-[var(--text-1)]">
                    ${Math.round(child.median_household_income).toLocaleString()}
                  </p>
                </div>
              )}
              {child.affordability_ratio != null && (
                <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-4">
                  <p className="text-xs font-medium text-[var(--text-2)] uppercase">Affordability</p>
                  <p className="mt-1 text-xl font-bold text-[var(--text-1)]">
                    {child.affordability_ratio.toFixed(1)}x
                  </p>
                  <p className="text-xs text-[var(--text-3)]">
                    {child.affordability_ratio <= 3.0 ? 'Very Affordable' :
                     child.affordability_ratio <= 4.5 ? 'Affordable' :
                     child.affordability_ratio <= 6.0 ? 'Moderate' : 'Expensive'}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Characteristics badges */}
        {hasCharacteristics && (
          <section className="space-y-3">
            <h3 className="text-xl font-bold text-[var(--text-1)]">Characteristics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Safety */}
              {child.crime_score != null && (
                <div className={`rounded-lg border p-3 ${
                  child.crime_score >= 70
                    ? 'border-emerald-400/30 bg-emerald-500/10'
                    : child.crime_score >= 40
                    ? 'border-yellow-400/30 bg-yellow-500/10'
                    : 'border-red-400/30 bg-red-500/10'
                }`}>
                  <p className={`font-semibold text-sm ${
                    child.crime_score >= 70 ? 'text-emerald-400' :
                    child.crime_score >= 40 ? 'text-yellow-400' : 'text-red-400'
                  }`}>Safety: {child.crime_score}/100</p>
                  <p className="text-xs text-[var(--text-2)] mt-0.5">
                    {child.crime_score >= 85 ? 'Very Safe' :
                     child.crime_score >= 70 ? 'Safe' :
                     child.crime_score >= 40 ? 'Average' : 'Higher Crime'}
                  </p>
                </div>
              )}

              {/* Schools */}
              {child.school_score != null && (
                <div className={`rounded-lg border p-3 ${
                  child.school_score >= 70
                    ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                    : 'border-[var(--border)] bg-[var(--surface-1)]'
                }`}>
                  <p className={`font-semibold text-sm ${
                    child.school_score >= 70 ? 'text-blue-900/80 dark:text-blue-300' : 'text-[var(--text-1)]'
                  }`}>Schools: {child.school_score}/100</p>
                  <p className="text-xs text-[var(--text-2)] mt-0.5">
                    {child.graduation_rate != null ? `${child.graduation_rate}% grad rate` : ''}
                    {child.graduation_rate != null && child.student_teacher_ratio != null ? ' · ' : ''}
                    {child.student_teacher_ratio != null ? `${child.student_teacher_ratio}:1` : ''}
                  </p>
                </div>
              )}

              {/* Air Quality */}
              {child.epa_aqi_median != null && (
                <div className={`rounded-lg border p-3 ${
                  child.epa_aqi_median <= 35
                    ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                    : 'border-[var(--border)] bg-[var(--surface-1)]'
                }`}>
                  <p className={`font-semibold text-sm ${
                    child.epa_aqi_median <= 35 ? 'text-blue-900/80 dark:text-blue-300' : 'text-[var(--text-1)]'
                  }`}>Air Quality: AQI {child.epa_aqi_median}</p>
                  <p className="text-xs text-[var(--text-2)] mt-0.5">
                    {child.epa_aqi_median <= 30 ? 'Excellent' :
                     child.epa_aqi_median <= 40 ? 'Good' :
                     child.epa_aqi_median <= 50 ? 'Moderate' : 'Fair'}
                  </p>
                </div>
              )}

              {/* LGBTQ */}
              {child.lgbtq_score != null && (
                <div className={`rounded-lg border p-3 ${
                  child.lgbtq_score >= 60
                    ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                    : 'border-[var(--border)] bg-[var(--surface-1)]'
                }`}>
                  <p className={`font-semibold text-sm ${
                    child.lgbtq_score >= 60 ? 'text-blue-900/80 dark:text-blue-300' : 'text-[var(--text-1)]'
                  }`}>LGBTQ Equality: {child.lgbtq_score}/100</p>
                  <p className="text-xs text-[var(--text-2)] mt-0.5">
                    {child.lgbtq_score >= 80 ? 'Very Inclusive' :
                     child.lgbtq_score >= 60 ? 'Inclusive' :
                     child.lgbtq_score >= 40 ? 'Mixed' : 'Limited'}
                  </p>
                </div>
              )}

              {/* Transit/Walkability */}
              {(() => {
                const transit = child.pct_public_transit ?? 0
                const walkBike = child.pct_walk_bike ?? 0
                const combined = transit + walkBike
                if (combined <= 0) return null
                return (
                  <div className={`rounded-lg border p-3 ${
                    combined >= 10
                      ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                      : 'border-[var(--border)] bg-[var(--surface-1)]'
                  }`}>
                    <p className={`font-semibold text-sm ${
                      combined >= 10 ? 'text-blue-900/80 dark:text-blue-300' : 'text-[var(--text-1)]'
                    }`}>Walkability & Transit</p>
                    <p className="text-xs text-[var(--text-2)] mt-0.5">
                      {transit > 0 ? `${transit.toFixed(1)}% transit` : ''}
                      {transit > 0 && walkBike > 0 ? ' · ' : ''}
                      {walkBike > 0 ? `${walkBike.toFixed(1)}% walk/bike` : ''}
                    </p>
                  </div>
                )
              })()}

              {/* VA Distance */}
              {child.nearest_va_distance_miles != null && (
                <div className={`rounded-lg border p-3 ${
                  child.nearest_va_distance_miles <= 30
                    ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                    : 'border-[var(--border)] bg-[var(--surface-1)]'
                }`}>
                  <p className={`font-semibold text-sm ${
                    child.nearest_va_distance_miles <= 30 ? 'text-blue-900/80 dark:text-blue-300' : 'text-[var(--text-1)]'
                  }`}>Nearest VA: {child.nearest_va_distance_miles} mi</p>
                  <p className="text-xs text-[var(--text-2)] mt-0.5">
                    {child.nearest_va_facility_name || 'VA Health Facility'}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* No data message */}
        {!hasEconomicData && !hasCharacteristics && !child.description && (
          <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-6 text-center">
            <p className="text-[var(--text-2)]">
              Detailed data for {child.primary_city} is still being collected.
              You can still run a financial scenario to explore costs and housing.
            </p>
          </div>
        )}

        {/* CTA buttons */}
        <section className="flex gap-3 pt-2">
          <button
            onClick={() => {
              if (child.primary_zip) {
                router.push(`/calculator/transition?targetZip=${encodeURIComponent(child.primary_zip)}`)
              }
            }}
            className="flex-1 rounded-lg bg-[var(--accent)] px-6 py-3 text-lg font-semibold text-[var(--accent-contrast)] transition hover:bg-[var(--accent-hover)]"
          >
            Start Financial Scenario
          </button>
          <button
            onClick={() => setSelectedChild(null)}
            className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-6 py-3 text-lg font-semibold text-[var(--text-1)] transition hover:bg-[var(--surface-2)]"
          >
            Back
          </button>
        </section>
      </div>
    )
  }

  /* ── Enhanced Area Picker (10+ areas) ────────────── */
  const EnhancedAreaPicker = () => (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[var(--text-1)]">
          Choose an Area in {city.primary_city}
        </h2>
        <span className="text-sm text-[var(--text-3)]">
          {filteredAreas.length} area{filteredAreas.length !== 1 ? 's' : ''}
        </span>
      </div>

      <p className="text-[var(--text-2)]">
        {city.primary_city} has {city.child_count} distinct areas. Select one to run a personalized financial scenario.
      </p>

      {/* Search + Sort controls */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-3)]" />
          <input
            type="text"
            placeholder="Search areas..."
            value={areaSearch}
            onChange={(e) => setAreaSearch(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-1)] py-2.5 pl-10 pr-4 text-sm text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)]"
          />
        </div>
        <div className="relative">
          <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-3)] pointer-events-none" />
          <select
            value={areaSort}
            onChange={(e) => setAreaSort(e.target.value as SortKey)}
            className="appearance-none rounded-lg border border-[var(--border)] bg-[var(--surface-1)] py-2.5 pl-10 pr-8 text-sm text-[var(--text-1)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)] cursor-pointer"
          >
            <option value="name">A → Z</option>
            <option value="price-asc">Price: Low → High</option>
            <option value="price-desc">Price: High → Low</option>
          </select>
        </div>
      </div>

      {/* Area grid */}
      {loadingChildren && !childCities && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--accent)] border-t-transparent" />
          <span className="ml-3 text-[var(--text-3)]">Loading areas...</span>
        </div>
      )}

      {childCities && filteredAreas.length === 0 && (
        <div className="text-center py-8 text-[var(--text-3)]">
          No areas match &ldquo;{areaSearch}&rdquo;
        </div>
      )}

      {filteredAreas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[480px] overflow-y-auto pr-1">
          {filteredAreas.map((child) => (
            <AreaCard key={child.id} child={child} />
          ))}
        </div>
      )}
    </section>
  )

  /* ── Inline Area List (2-9 areas) ────────────────── */
  const InlineAreaList = () => (
    <section className="space-y-4">
      <h2 className="text-2xl font-bold text-[var(--text-1)]">
        Areas in {city.primary_city} ({city.child_count})
      </h2>

      {loadingChildren && !childCities && (
        <p className="text-[var(--text-3)]">Loading areas...</p>
      )}

      {childCities && childCities.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {childCities
            .sort((a, b) => a.primary_city.localeCompare(b.primary_city))
            .map((child) => (
              <AreaCard key={child.id} child={child} />
            ))}
        </div>
      )}
    </section>
  )

  /* ── City Overview (collapsible for large cities) ── */
  const CityOverviewContent = () => (
    <>
      {/* About + Map Section */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {description && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[var(--text-1)]">About {city.primary_city}</h2>
            <p className="text-[var(--text-2)] leading-relaxed text-lg">{description}</p>
          </div>
        )}
        {city.lat != null && city.lng != null && (
          <div className={description ? '' : 'md:col-span-2'}>
            <CityLocationMap
              lat={city.lat}
              lng={city.lng}
              cityName={`${city.primary_city}, ${city.primary_state}`}
              className="h-full min-h-[280px]"
            />
          </div>
        )}
      </section>

      {/* Weather Strip */}
      {climate && <WeatherStrip climate={climate} />}

      {/* Economic Data Section */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-[var(--text-1)]">Economic Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-6">
            <p className="text-xs font-medium text-[var(--text-2)] uppercase">Median Home Price</p>
            <p className="mt-2 text-2xl font-bold text-[var(--text-1)]">
              {city.median_home_price ? `$${Math.round(city.median_home_price).toLocaleString()}` : '—'}
            </p>
          </div>

          <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-6">
            <p className="text-xs font-medium text-[var(--text-2)] uppercase">State Income Tax</p>
            <p className="mt-2 text-2xl font-bold text-[var(--text-1)]">
              {city.no_state_income_tax ? '✓ None' : 'Taxed'}
            </p>
          </div>

          <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-6">
            <p className="text-xs font-medium text-[var(--text-2)] uppercase">Household Income</p>
            <p className="mt-2 text-2xl font-bold text-[var(--text-1)]">
              {city.median_household_income ? `$${Math.round(city.median_household_income).toLocaleString()}` : '—'}
            </p>
          </div>
        </div>
      </section>

      {/* City Features Section */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-[var(--text-1)]">City Characteristics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {city.veterans_top_50_rank !== null && (
            <div className="rounded-lg border border-blue-900/15 bg-blue-900/5 p-4 dark:border-blue-300/15 dark:bg-blue-300/5">
              <p className="font-semibold text-blue-900/80 dark:text-blue-300">⭐ Top 50 Vet City (#{city.veterans_top_50_rank})</p>
            </div>
          )}
          {city.no_state_income_tax && (
            <div className="rounded-lg border border-blue-900/15 bg-blue-900/5 p-4 dark:border-blue-300/15 dark:bg-blue-300/5">
              <p className="font-semibold text-blue-900/80 dark:text-blue-300">💸 No State Tax</p>
            </div>
          )}
          {city.affordability_ratio != null && (
            <div className={`rounded-lg border p-4 ${
              city.affordability_ratio >= 2.75 && city.affordability_ratio <= 4.5
                ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                : 'border-[var(--border)] bg-[var(--surface-1)]'
            }`}>
              <p className={`font-semibold ${
                city.affordability_ratio >= 2.75 && city.affordability_ratio <= 4.5
                  ? 'text-blue-900/80 dark:text-blue-300'
                  : 'text-[var(--text-1)]'
              }`}>🏠 Housing Affordability</p>
              <p className="text-xs text-[var(--text-2)] mt-1">
                {city.affordability_ratio.toFixed(1)}x income
                {city.affordability_ratio <= 3.0 ? ' — Very Affordable' :
                 city.affordability_ratio <= 4.5 ? ' — Affordable' :
                 city.affordability_ratio <= 6.0 ? ' — Moderate' : ' — Expensive'}
              </p>
            </div>
          )}
          {city.income_percentile != null && city.income_percentile >= 85 && (
            <div className="rounded-lg border border-blue-900/15 bg-blue-900/5 p-4 dark:border-blue-300/15 dark:bg-blue-300/5">
              <p className="font-semibold text-blue-900/80 dark:text-blue-300">💰 High Income Area</p>
              <p className="text-xs text-[var(--text-2)] mt-1">Top {(100 - city.income_percentile).toFixed(0)}% nationally</p>
            </div>
          )}
          {(() => {
            const pop = city.population ?? city.rank_population
            return pop != null && pop >= 500_000 ? (
              <div className="rounded-lg border border-blue-900/15 bg-blue-900/5 p-4 dark:border-blue-300/15 dark:bg-blue-300/5">
                <p className="font-semibold text-blue-900/80 dark:text-blue-300">🏙️ Big City</p>
                <p className="text-xs text-[var(--text-2)] mt-1">Pop. {formatPopulation(pop)}</p>
              </div>
            ) : null
          })()}
          {(() => {
            const pop = city.population ?? city.rank_population
            return pop != null && pop < 50_000 ? (
              <div className="rounded-lg border border-blue-900/15 bg-blue-900/5 p-4 dark:border-blue-300/15 dark:bg-blue-300/5">
                <p className="font-semibold text-blue-900/80 dark:text-blue-300">🏡 Small Town</p>
                <p className="text-xs text-[var(--text-2)] mt-1">Pop. {formatPopulation(pop)}</p>
              </div>
            ) : null
          })()}
          {city.tech_hub && (
            <div className="rounded-lg border border-blue-900/15 bg-blue-900/5 p-4 dark:border-blue-300/15 dark:bg-blue-300/5">
              <p className="font-semibold text-blue-900/80 dark:text-blue-300">🧠 Tech Hub</p>
            </div>
          )}
          {city.military_hub && (
            <div className="rounded-lg border border-blue-900/15 bg-blue-900/5 p-4 dark:border-blue-300/15 dark:bg-blue-300/5">
              <p className="font-semibold text-blue-900/80 dark:text-blue-300">🪖 Military Hub</p>
            </div>
          )}
          {city.gun_friendly && (
            <div className="rounded-lg border border-blue-900/15 bg-blue-900/5 p-4 dark:border-blue-300/15 dark:bg-blue-300/5">
              <p className="font-semibold text-blue-900/80 dark:text-blue-300">🔫 Pro-Gun</p>
            </div>
          )}
          {city.near_va_medical && (
            <div className="rounded-lg border border-blue-900/15 bg-blue-900/5 p-4 dark:border-blue-300/15 dark:bg-blue-300/5">
              <p className="font-semibold text-blue-900/80 dark:text-blue-300">🩺 Near VA Hospital</p>
            </div>
          )}
          {city.lgbtq_score != null && (
            <div className={`rounded-lg border p-4 ${
              city.lgbtq_score >= 60
                ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                : 'border-[var(--border)] bg-[var(--surface-1)]'
            }`}>
              <p className={`font-semibold ${
                city.lgbtq_score >= 60 ? 'text-blue-900/80 dark:text-blue-300' : 'text-[var(--text-1)]'
              }`}>🏳️‍🌈 LGBTQ Equality</p>
              <p className="text-xs text-[var(--text-2)] mt-1">
                Score: {city.lgbtq_score}/100
                {city.lgbtq_score >= 80 ? ' — Very Inclusive' :
                 city.lgbtq_score >= 60 ? ' — Inclusive' :
                 city.lgbtq_score >= 40 ? ' — Mixed' : ' — Limited Protections'}
              </p>
            </div>
          )}
          {city.epa_aqi_median != null && (
            <div className={`rounded-lg border p-4 ${
              city.epa_aqi_median <= 35
                ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                : 'border-[var(--border)] bg-[var(--surface-1)]'
            }`}>
              <p className={`font-semibold ${
                city.epa_aqi_median <= 35 ? 'text-blue-900/80 dark:text-blue-300' : 'text-[var(--text-1)]'
              }`}>🌿 Air Quality</p>
              <p className="text-xs text-[var(--text-2)] mt-1">
                AQI: {city.epa_aqi_median}
                {city.epa_aqi_median <= 30 ? ' — Excellent' :
                 city.epa_aqi_median <= 40 ? ' — Good' :
                 city.epa_aqi_median <= 50 ? ' — Moderate' : ' — Fair'}
              </p>
            </div>
          )}
          {(() => {
            const transit = city.pct_public_transit ?? 0
            const walkBike = city.pct_walk_bike ?? 0
            const combined = transit + walkBike
            if (combined <= 0) return null
            const isWalkable = combined >= 10
            return (
              <div className={`rounded-lg border p-4 ${
                isWalkable
                  ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                  : 'border-[var(--border)] bg-[var(--surface-1)]'
              }`}>
                <p className={`font-semibold ${
                  isWalkable ? 'text-blue-900/80 dark:text-blue-300' : 'text-[var(--text-1)]'
                }`}>🚶 Walkability & Transit</p>
                <p className="text-xs text-[var(--text-2)] mt-1">
                  {transit > 0 ? `${transit.toFixed(1)}% transit` : ''}
                  {transit > 0 && walkBike > 0 ? ' · ' : ''}
                  {walkBike > 0 ? `${walkBike.toFixed(1)}% walk/bike` : ''}
                  {combined >= 25 ? ' — Very Walkable' :
                   combined >= 15 ? ' — Walkable' :
                   combined >= 10 ? ' — Moderate' : ' — Car-Dependent'}
                </p>
              </div>
            )
          })()}
          {/* Crime / Safety badge */}
          {city.crime_score != null && (
            <div className={`rounded-lg border p-4 ${
              city.crime_score >= 70
                ? 'border-emerald-400/30 bg-emerald-500/10'
                : city.crime_score >= 40
                ? 'border-yellow-400/30 bg-yellow-500/10'
                : 'border-red-400/30 bg-red-500/10'
            }`}>
              <p className={`font-semibold ${
                city.crime_score >= 70 ? 'text-emerald-400' :
                city.crime_score >= 40 ? 'text-yellow-400' : 'text-red-400'
              }`}>🛡️ Safety Score: {city.crime_score}/100</p>
              <p className="text-xs text-[var(--text-2)] mt-1">
                {city.violent_crime_rate != null ? `Violent: ${city.violent_crime_rate}/100k` : ''}
                {city.violent_crime_rate != null && city.property_crime_rate != null ? ' · ' : ''}
                {city.property_crime_rate != null ? `Property: ${city.property_crime_rate}/100k` : ''}
                {city.crime_score >= 85 ? ' — Very Safe' :
                 city.crime_score >= 70 ? ' — Safe' :
                 city.crime_score >= 40 ? ' — Average' : ' — Higher Crime'}
              </p>
            </div>
          )}
          {/* School Performance badge */}
          {(city.school_score != null || city.student_teacher_ratio != null || city.graduation_rate != null || city.per_pupil_spending != null) && (
            <div className={`rounded-lg border p-4 ${
              city.school_score != null && city.school_score >= 70
                ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                : city.school_score != null && city.school_score >= 40
                ? 'border-blue-900/10 bg-blue-900/[0.02] dark:border-blue-300/10 dark:bg-blue-300/[0.02]'
                : 'border-[var(--border)] bg-[var(--surface-1)]'
            }`}>
              <p className={`font-semibold ${
                city.school_score != null && city.school_score >= 70 ? 'text-blue-900/80 dark:text-blue-300' :
                city.school_score != null && city.school_score >= 40 ? 'text-blue-900/60 dark:text-blue-300/70' : 'text-[var(--text-1)]'
              }`}>🎓 Schools{city.school_score != null ? `: ${city.school_score}/100` : ''}</p>
              <p className="text-xs text-[var(--text-2)] mt-1">
                {city.graduation_rate != null ? `Grad rate: ${city.graduation_rate}%` : ''}
                {city.graduation_rate != null && city.student_teacher_ratio != null ? ' · ' : ''}
                {city.student_teacher_ratio != null ? `${city.student_teacher_ratio}:1 ratio` : ''}
                {city.per_pupil_spending != null ? ` · $${Math.round(city.per_pupil_spending).toLocaleString()}/pupil` : ''}
                {city.school_score == null && city.student_teacher_ratio == null && city.graduation_rate == null && city.per_pupil_spending == null ? 'No data available' : ''}
              </p>
            </div>
          )}
          {/* VA Facility Distance badge */}
          {city.nearest_va_distance_miles != null && (
            <div className={`rounded-lg border p-4 ${
              city.nearest_va_distance_miles <= 30
                ? 'border-blue-900/15 bg-blue-900/5 dark:border-blue-300/15 dark:bg-blue-300/5'
                : 'border-[var(--border)] bg-[var(--surface-1)]'
            }`}>
              <p className={`font-semibold ${
                city.nearest_va_distance_miles <= 30 ? 'text-blue-900/80 dark:text-blue-300' : 'text-[var(--text-1)]'
              }`}>🏥 Nearest VA: {city.nearest_va_distance_miles} mi</p>
              <p className="text-xs text-[var(--text-2)] mt-1">
                {city.nearest_va_facility_name || 'VA Health Facility'}
                {city.nearest_va_distance_miles <= 15 ? ' — Very Close' :
                 city.nearest_va_distance_miles <= 30 ? ' — Nearby' :
                 city.nearest_va_distance_miles <= 60 ? ' — Moderate Distance' : ' — Far'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Professional Sports */}
      {(city.sports.nfl ||
        city.sports.nba ||
        city.sports.mlb ||
        city.sports.nhl ||
        city.sports.mls) && (
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-[var(--text-1)]">Professional Sports</h2>
          <div className="text-4xl space-x-4 p-6 rounded-lg border border-[var(--border)] bg-[var(--surface-1)]">
            {city.sports.nfl && <span title="NFL">🏈</span>}
            {city.sports.nba && <span title="NBA">🏀</span>}
            {city.sports.mlb && <span title="MLB">⚾</span>}
            {city.sports.nhl && <span title="NHL">🏒</span>}
            {city.sports.mls && <span title="MLS">⚽</span>}
          </div>
        </section>
      )}
    </>
  )

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
      {/* Close button (fixed) */}
      <button
        onClick={onClose}
        className="fixed top-4 right-4 rounded-full bg-white p-2 text-gray-600 hover:bg-gray-100 transition"
        style={{ zIndex: 9999 }}
      >
        <X className="h-6 w-6" />
      </button>

      <div className="min-h-screen bg-[var(--bg)]">
        {/* Hero Section */}
        {city.hero_image_url && (
          <div className={`relative bg-gradient-to-br from-[var(--surface-2)] via-[var(--surface-1)] to-[var(--surface-3)] ${
            isManyAreas ? 'h-64' : 'h-96'
          }`}>
            <img
              src={city.hero_image_url}
              alt={`${city.primary_city} skyline`}
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

            {/* Header overlay on image */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                {city.primary_city}, {city.primary_state}
              </h1>
              <p className="text-lg text-white/80">
                Population: {formatPopulation(city.population ?? city.rank_population)}
              </p>
            </div>
          </div>
        )}

        {/* Content — layout changes based on tier */}
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">

          {/* ═══ TIER: MANY AREAS (10+) ═══════════════════
              Area picker is the PRIMARY content.
              City overview is collapsible below.
          */}
          {isManyAreas && (
            <>
              {selectedChild ? (
                /* ── Child detail view ── */
                <ChildCityDetail child={selectedChild} />
              ) : (
                <>
                  {/* About + Map pulled up above areas for context */}
                  <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {description && (
                      <div className="space-y-4">
                        <h2 className="text-2xl font-bold text-[var(--text-1)]">About {city.primary_city}</h2>
                        <p className="text-[var(--text-2)] leading-relaxed text-lg">{description}</p>
                      </div>
                    )}
                    {city.lat != null && city.lng != null && (
                      <div className={description ? '' : 'md:col-span-2'}>
                        <CityLocationMap
                          lat={city.lat}
                          lng={city.lng}
                          cityName={`${city.primary_city}, ${city.primary_state}`}
                          className="h-full min-h-[280px]"
                        />
                      </div>
                    )}
                  </section>

                  <EnhancedAreaPicker />

                  {/* Collapsible city overview */}
                  <section>
                    <button
                      onClick={() => setShowCityOverview((prev) => !prev)}
                      className="flex items-center gap-2 text-xl font-bold text-[var(--text-1)] hover:text-[var(--accent)] transition"
                    >
                      {showCityOverview ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      City Overview
                    </button>

                    {showCityOverview && (
                      <div className="mt-6 space-y-8">
                        <CityOverviewContent />
                      </div>
                    )}
                  </section>

                  {/* Close button only — no "Start Financial Scenario" for many-area cities */}
                  <section className="py-4">
                    <button
                      onClick={onClose}
                      className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-6 py-3 text-lg font-semibold text-[var(--text-1)] transition hover:bg-[var(--surface-2)]"
                    >
                      Close
                    </button>
                  </section>
                </>
              )}
            </>
          )}

          {/* ═══ TIER: FEW AREAS (2-9) ════════════════════
              Normal layout with areas shown inline (no accordion).
          */}
          {!isManyAreas && hasChildren && (
            <>
              {selectedChild ? (
                <ChildCityDetail child={selectedChild} />
              ) : (
                <>
                  <CityOverviewContent />

                  <InlineAreaList />

                  {/* CTA Section */}
                  <section className="flex gap-4 py-8">
                    <button
                      onClick={handleStartScenario}
                      className="flex-1 rounded-lg bg-[var(--accent)] px-6 py-3 text-lg font-semibold text-[var(--accent-contrast)] transition hover:bg-[var(--accent-hover)]"
                    >
                      Start Financial Scenario
                    </button>
                    <button
                      onClick={onClose}
                      className="flex-1 rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-6 py-3 text-lg font-semibold text-[var(--text-1)] transition hover:bg-[var(--surface-2)]"
                    >
                      Close
                    </button>
                  </section>
                </>
              )}
            </>
          )}

          {/* ═══ TIER: SINGLE AREA (0-1) ══════════════════
              No area picker — go straight to scenario.
          */}
          {!hasChildren && (
            <>
              <CityOverviewContent />

              {/* CTA Section */}
              <section className="flex gap-4 py-8">
                <button
                  onClick={handleStartScenario}
                  className="flex-1 rounded-lg bg-[var(--accent)] px-6 py-3 text-lg font-semibold text-[var(--accent-contrast)] transition hover:bg-[var(--accent-hover)]"
                >
                  Start Financial Scenario
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-6 py-3 text-lg font-semibold text-[var(--text-1)] transition hover:bg-[var(--surface-2)]"
                >
                  Close
                </button>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
